import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowUpRight, FiChevronDown, FiArrowRight, FiVideo, FiPhone, FiCode } from 'react-icons/fi';
import { FaRobot, FaCode, FaBrain, FaSync, FaHeadset, FaChartLine, FaCloud, FaGlobe, FaMobileAlt } from 'react-icons/fa';
import CTAButton from './CTAButton';
import { useAIPrompt } from '../context/AIPromptContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [trianglePosition, setTrianglePosition] = useState(0);
  const dropdownRef = useRef(null);
  const desktopButtonRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isContactPage = location.pathname === '/contact';
  const { togglePromptMode } = useAIPrompt();

  useEffect(() => {
    if (isServicesOpen && desktopButtonRef.current) {
      const rect = desktopButtonRef.current.getBoundingClientRect();
      setTrianglePosition(rect.left + rect.width / 2);
    }
  }, [isServicesOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDesktopTrigger = desktopButtonRef.current && desktopButtonRef.current.contains(event.target);
      const isMobileTrigger = mobileButtonRef.current && mobileButtonRef.current.contains(event.target);
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);

      if (!isDesktopTrigger && !isMobileTrigger && !isInsideDropdown) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const services = [
    { label: 'AI Development', href: '/services/ai-development', icon: <FaBrain /> },
    { label: 'Media Technology', href: '/services/media-technology', icon: <FiVideo /> },
    { label: 'Voice & Telecom', href: '/services/voice-telecom', icon: <FiPhone /> },
    { label: 'AI for Startups', href: '/services/ai-for-startups', icon: <FaRobot /> },
    { label: 'Vibe Coding Cleanup', href: '/services/vibe-coding', icon: <FaCode /> },
    { label: 'AI/ML & Generative AI', href: '/services/ai-ml-generative', icon: <FaBrain /> },
    { label: 'Digital Transformation', href: '/services/digital-transformation', icon: <FaSync /> },
    { label: 'Consulting', href: '/services/consulting', icon: <FaHeadset /> },
    { label: 'Business Intelligence', href: '/services/business-intelligence', icon: <FaChartLine /> },
    { label: 'Cloud Development', href: '/services/cloud-development', icon: <FaCloud /> },
    { label: 'Web Development', href: '/services/web-development', icon: <FaGlobe /> },
    { label: 'Mobile Development', href: '/services/mobile-development', icon: <FaMobileAlt /> }
  ];

  const handleContactClick = () => {
    navigate('/contact');
  };

  const handlePortfolioClick = () => {
    navigate('/portfolio');
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  };

  const handleServiceClick = (href) => {
    navigate(href);
    setIsServicesOpen(false);
    setIsMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[99] transition-all py-2 duration-300 ${
        isScrolled ? 'bg-[#fefdfb] border-b border-black' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <div
          className="cursor-pointer group"
          onClick={() => navigate('/')}
        >
          <span className="text-3xl md:text-5xl font-bold tracking-tight text-black group-hover:opacity-75 transition-opacity">
            .BERCASTLE
          </span>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <button
            onClick={handlePortfolioClick}
            className="text-sm font-medium text-black hover:opacity-60 transition-opacity"
          >
            Portfolio
          </button>
          <div className="relative">
            <button
              ref={desktopButtonRef}
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              className="text-sm font-medium text-black hover:opacity-60 transition-opacity flex items-center gap-1"
            >
              Services
              <FiChevronDown className={`transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {!isContactPage && (
            <CTAButton onClick={handleContactClick}>
              Contact us
            </CTAButton>
          )}
        </nav>

        {/* Mobile Hamburger Menu */}
        <div className="md:hidden flex items-center gap-4">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="w-8 h-8 flex flex-col items-center justify-center gap-1.5 group"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <div
              className={`w-6 h-0.5 bg-black transition-transform duration-300 origin-center ${
                isMenuOpen ? 'translate-y-2 rotate-45' : ''
              }`}
            />
            <div
              className={`w-6 h-0.5 bg-black transition-opacity duration-300 ${
                isMenuOpen ? 'opacity-0' : ''
              }`}
            />
            <div
              className={`w-6 h-0.5 bg-black transition-transform duration-300 origin-center ${
                isMenuOpen ? '-translate-y-2 -rotate-45' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {/* Desktop Services Dropdown */}
      <div ref={dropdownRef} onClick={() => setIsServicesOpen(false)}>
        {isServicesOpen && (
          <>

            <div className="hidden md:block fixed inset-x-0 mx-auto max-w-7xl bg-black z-40 animate-slideDown rounded-b-lg" style={{ top: '80px' }}>
              <div className="py-12 px-12">
                <div className="grid grid-cols-3 gap-8">
                  {services.map((service, index) => (
                    <button
                      key={service.href}
                      onClick={() => handleServiceClick(service.href)}
                      className="group text-left text-white hover:text-[#d6fa70] transition-all duration-300 text-xl font-medium flex items-center gap-4"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="text-gray-500 group-hover:text-[#d6fa70] group-hover:scale-110 transition-all duration-300">
                        {service.icon}
                      </div>
                      <span>{service.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <nav className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-col gap-3">
            <button
              onClick={handlePortfolioClick}
              className="w-full text-left text-sm font-medium text-black hover:opacity-60 transition-opacity py-2"
            >
              Portfolio
            </button>
            <div>
              <button
                ref={mobileButtonRef}
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="w-full text-left text-sm font-medium text-black hover:opacity-60 transition-opacity py-2 flex items-center justify-between"
              >
                Services
                <FiChevronDown className={`transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isServicesOpen && (
                <div className="pl-4 mt-2 space-y-2">
                  {services.map((service) => (
                    <button
                      key={service.href}
                      onClick={() => handleServiceClick(service.href)}
                      className="w-full text-left text-sm text-slate-600 hover:text-black group transition-colors py-2 flex items-center gap-3"
                    >
                      <span className="text-gray-400 group-hover:text-black transition-colors">{service.icon}</span>
                      {service.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!isContactPage && (
              <CTAButton
                onClick={() => {
                  handleContactClick();
                  setIsMenuOpen(false);
                }}
                className="w-full mt-2"
              >
                Contact us
              </CTAButton>
            )}
          </nav>
        </div>
      )}

      {/* Fixed AI Prompt Button */}
      <button
        onClick={togglePromptMode}
        className="fixed bottom-6 left-6 p-3 rounded-lg bg-black hover:bg-gray-800 text-[#d6fa70] transition-all hover:scale-110 z-50 shadow-lg"
        aria-label="View AI Prompt"
        title="Generate AI Prompt"
      >
        <FiCode />
      </button>
    </header>
  );
};

export default Header;
